import isEmpty from "lodash/isEmpty";
import { IStructuredTemplateSection } from "models/structuredTemplates.models";
import { getStructuredSectionPath } from "utils/helpers";
import { TStructuredTemplateTreeItem } from "./StructuredTemplateContext.types";

export const getTreeViewItemsByPath = (
  treeViewItems: TStructuredTemplateTreeItem[],
  treeViewItemMap = new Map<string, TStructuredTemplateTreeItem>(),
) => {
  treeViewItems.forEach((treeViewItem) => {
    treeViewItemMap.set(treeViewItem.path, treeViewItem);

    if (treeViewItem.children.length > 0) {
      getTreeViewItemsByPath(treeViewItem.children, treeViewItemMap);
    }
  });

  return treeViewItemMap;
};

export const getTreeItemsFromSections = (
  sections: IStructuredTemplateSection[],
  treeViewItemsByPath: Map<string, TStructuredTemplateTreeItem>,
  defaultSectionsCollapsed = false,
  parentPath?: string,
): TStructuredTemplateTreeItem[] => {
  return sections.map((section, index) => {
    const path = getStructuredSectionPath(parentPath, index);

    const oldTreeViewItem = treeViewItemsByPath.get(path);

    const children = getTreeItemsFromSections(
      section.sections,
      treeViewItemsByPath,
      defaultSectionsCollapsed,
      path,
    );

    const { id, name } = section;

    return {
      // Sortable tree identity is the UI path; BE identity stays in sectionId.
      id: path,
      sectionId: id,
      name,
      path,
      // If the action done was to add or remove a subsection from a collapsed item, uncollapse it
      // so the user can see the result of the action
      collapsed:
        oldTreeViewItem?.collapsed !== undefined
          ? oldTreeViewItem.collapsed &&
            oldTreeViewItem.children.length === children.length
          : defaultSectionsCollapsed
            ? children.length > 0
            : undefined,
      children,
    };
  });
};

/**
 * This takes the items from the format the sortable tree is using and puts it back into the format
 * that the BE expects.
 */
export const getSectionsFromTreeItems = (
  treeViewItems: TStructuredTemplateTreeItem[],
): IStructuredTemplateSection[] => {
  return treeViewItems.map((treeViewItem) => {
    const { sectionId, name } = treeViewItem;

    return {
      ...(sectionId ? { id: sectionId } : {}),
      name,
      sections: getSectionsFromTreeItems(treeViewItem.children),
    } as IStructuredTemplateSection;
  });
};

export const recalculateTreeItemPaths = (
  treeViewItems: TStructuredTemplateTreeItem[],
  parentPath?: string,
): TStructuredTemplateTreeItem[] =>
  treeViewItems.map((treeViewItem, index) => {
    const path = getStructuredSectionPath(parentPath, index);

    return {
      ...treeViewItem,
      // Keep the sortable adapter id aligned with the recalculated UI path after moves.
      id: path,
      path,
      children: recalculateTreeItemPaths(treeViewItem.children, path),
    };
  });

/**
 * When the user clicks on a collapse button in a row, this ensures that all of the children also
 * get recursively collapsed. When the user drops an item onto a collapsed parent as a child, it
 * also expands that section so they can see the result of the drop. Other sub-sections remain
 * collapsed.
 */
export const recursivelyCollapse = (
  treeViewItems: TStructuredTemplateTreeItem[],
  dropParentPath: string | null,
  isCollapsed = false,
): TStructuredTemplateTreeItem[] =>
  treeViewItems.map((treeViewItem) => ({
    ...treeViewItem,
    collapsed:
      treeViewItem.path === dropParentPath || isEmpty(treeViewItem.children)
        ? false
        : treeViewItem.collapsed || isCollapsed,
    children: recursivelyCollapse(
      treeViewItem.children,
      dropParentPath,
      treeViewItem.collapsed || isCollapsed,
    ),
  }));
